/**
 * Timezone-aware date formatting utilities.
 *
 * Every formatter takes an options object whose `timeZone` is REQUIRED —
 * an IANA zone (e.g. "Europe/Stockholm"), or `null` for the browser's local
 * zone. The field being mandatory (and named) is the point: the app boxes run
 * `TZ=Etc/UTC`, users do not, so a formatter that silently falls back to the
 * process zone renders different text on the server pass and at hydration,
 * which React reports as a text mismatch (#418) and repairs by throwing the
 * server HTML away. Two positional string parameters also let a locale land in
 * the timeZone slot unnoticed (prod 2026-09-01: `RangeError: Invalid time zone
 * specified: en`); a named field makes that unrepresentable.
 *
 * `timeZone: null` (browser-local) is only legal for text that NEVER renders
 * on the server — i.e. behind a mounted/hydration gate such as `useNow()` or
 * `useHydrated()`. Callers under `/a/<slug>` get the instance's zone from
 * `useInstanceTimeZone()` (lib/instance-timezone.tsx).
 */

import type { Locale } from "@/lib/i18n/locales";

/** BCP-47 tag per site locale. A `Record` so a new locale is a compile error
 *  here rather than a raw `"de"` reaching Intl and formatting who-knows-how. */
const DATE_LOCALES: Record<Locale, string> = { en: "en-US", sv: "sv-SE", de: "de-DE" };

/**
 * A bare calendar date ("2026-09-25") written out in the reader's language.
 *
 * `timeZone: "UTC"` is load-bearing, not boilerplate. The input has no zone, so
 * `new Date()` parses it as UTC midnight and formatting it in any zone behind
 * UTC yields the PREVIOUS day — a date of the 25th renders "September 24" for a
 * reader in New York, and differently again on the boxes, which run TZ=UTC
 * while their readers do not. That server/client disagreement is the #418
 * hydration mismatch this module exists to prevent.
 *
 * For published dates (blog posts, legal registers), where the date is a fact
 * about the document rather than a moment in the reader's day.
 */
const CALENDAR_FORMATTERS = new Map<Locale, Intl.DateTimeFormat>();

const calendarFormatter = (locale: Locale): Intl.DateTimeFormat => {
  const cached = CALENDAR_FORMATTERS.get(locale);
  if (cached !== undefined) return cached;
  // Constructing an Intl.DateTimeFormat is ~50x the cost of using one, and
  // these pages call this once per row: measured at 1.7ms vs 0.03ms for a
  // 27-call render. The options are constant, which is what makes it cacheable
  // — and there are three locales, so the map cannot grow.
  const made = new Intl.DateTimeFormat(DATE_LOCALES[locale], {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
  CALENDAR_FORMATTERS.set(locale, made);
  return made;
};

export const formatCalendarDate = (iso: string, locale: Locale): string =>
  calendarFormatter(locale).format(new Date(iso));

export interface FormatDateOptions {
  /** IANA zone to format in, or `null` for the browser's local zone (legal
   *  only for client-only text behind a hydration gate — see module doc). */
  timeZone: string | null;
  /** BCP 47 locale; defaults to "en-US". */
  locale?: string;
}

/** The zone to format in when an instance has never reported one
 *  (`instances.timezone IS NULL`). UTC is wrong for most users but *stable* —
 *  server and client agree on it, which is what keeps hydration intact. */
export const FALLBACK_TIME_ZONE = "UTC";

/** "Apr 18, 2026, 08:00" — times are 24-hour in every locale, never AM/PM. */
export function formatDateTime(
  date: Date | string,
  { timeZone, locale = "en-US" }: FormatDateOptions,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return String(date);
  return d.toLocaleString(locale, {
    timeZone: timeZone ?? undefined,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}

/** "Sep 24, 2026" in UTC — for admin surfaces and billing periods, which ARE
 *  UTC months. The pinned zone keeps the server pass (the boxes run TZ=UTC)
 *  and the browser rendering the same text. */
export const utcDay = (date: Date | string): string => formatDate(date, { timeZone: "UTC" });

const DAY_TIME_UTC = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

/** "Sep 24, 03:15 PM UTC" — the admin pages' timestamp. Dates render on the
 *  server too, and the boxes run TZ=UTC: an unzoned toLocale* call is a
 *  hydration mismatch waiting for a non-UTC browser. One formatter, built
 *  once: the admin tables call this per row. */
export const formatDayTimeUtc = (iso: string): string => `${DAY_TIME_UTC.format(new Date(iso))} UTC`;

/** "September 2026" in UTC — a billing period's month. */
export const utcMonth = (date: Date | string): string =>
  new Date(date).toLocaleDateString("en-US", { timeZone: "UTC", year: "numeric", month: "long" });

/** "Apr 18, 2026" */
export function formatDate(
  date: Date | string,
  { timeZone, locale = "en-US" }: FormatDateOptions,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return String(date);
  return d.toLocaleDateString(locale, {
    timeZone: timeZone ?? undefined,
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** "08:00" — 24-hour in every locale, never AM/PM. */
export function formatTime(
  date: Date | string,
  { timeZone, locale = "en-US" }: FormatDateOptions,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return String(date);
  return d.toLocaleTimeString(locale, {
    timeZone: timeZone ?? undefined,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}

/**
 * The calendar day an instant falls on in `timeZone`, as `YYYY-MM-DD`.
 *
 * This is the zone-aware replacement for reading `d.getFullYear()` /
 * `getMonth()` / `getDate()`, which answer in the *process* zone — UTC on the
 * boxes — and so decide "which day is this?" for the server rather than for the
 * user. Comparing two of these strings compares calendar days directly.
 */
export function calendarDayInZone(date: Date | string, timeZone: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  // en-CA renders ISO-ordered parts, but read them by type rather than by
  // position so a locale-data change can't silently swap day and month.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const part = (type: "year" | "month" | "day"): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** Whole calendar days from `from` to `to` as counted in `timeZone` — positive
 *  when `to` is the later day. */
export function calendarDaysApart(
  from: Date | string,
  to: Date | string,
  timeZone: string,
): number {
  const midnightUtc = (value: Date | string): number => {
    const [y, m, d] = calendarDayInZone(value, timeZone).split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((midnightUtc(to) - midnightUtc(from)) / 86_400_000);
}

/** "Fri, Apr 18" — short weekday + day + month */
export function formatShortDate(
  date: Date | string,
  { timeZone, locale = "en-US" }: FormatDateOptions,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return String(date);
  return d.toLocaleDateString(locale, {
    timeZone: timeZone ?? undefined,
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
